/**
 * Product Gallery Zoom Functionality
 * Compatible with Swiper.js galleries, excludes videos
 * Works on both desktop and mobile devices
 */

class ProductZoom {
  constructor() {
    this.isZoomed = false;
    this.currentImageIndex = 0;
    this.images = [];
    this.isMobile = window.innerWidth < 769;
    // Covers both mobile carousel slides and desktop stacked grid items.
    // visibleImages() filters this to only the currently visible gallery.
    this.imgSelector = '.pdp-gallery__slide img:not([src*=".thumbnail."]):not([src*="preview_images"]), .pdp-gallery-grid__item img:not([src*=".thumbnail."]):not([src*="preview_images"])';

    this.init();
    this.handleResize();
  }

  // Returns only images whose parent gallery element is currently visible.
  // display:none ancestors cause offsetParent to be null — so this correctly
  // excludes whichever of the two gallery elements is hidden at any viewport.
  visibleImages() {
    return Array.from(document.querySelectorAll(this.imgSelector))
      .filter(img => img.closest('.pdp-gallery--mobile, .pdp-gallery--desktop')?.offsetParent !== null);
  }

  handleResize() {
    window.addEventListener('resize', () => {
      const wasMobile = this.isMobile;
      this.isMobile = window.innerWidth < 769;

      if (wasMobile && !this.isMobile) {
        this.setupHoverZoom();
      }

      if (!wasMobile && this.isMobile) {
        this.removeCursors();
      }
    });
  }

  init() {
    this.createZoomModal();
    this.bindEvents();

    if (!this.isMobile) {
      this.setupHoverZoom();
    }
  }

  removeCursors() {
    // Only clear cursors on currently visible gallery images
    this.visibleImages().forEach(img => {
      img.style.cursor = '';
    });
  }

  createZoomModal() {
    if (document.getElementById('zoom-modal')) return;

    const modalHTML = `
      <div id="zoom-modal" class="zoom-modal" style="display: none;">
        <div class="zoom-modal-overlay"></div>
        <div class="zoom-modal-content">
          <button class="zoom-close" aria-label="Close zoom">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M18 6L6 18M6 6L18 18" stroke="currentColor" stroke-width="2" stroke-linecap="round"/>
            </svg>
          </button>
          <div class="zoom-thumbnails">
            <!-- Thumbnails will be populated dynamically -->
          </div>
          <div class="zoom-image-container">
            <img id="zoom-image" src="" alt="" />
            <div class="zoom-loading">Loading...</div>
          </div>
        </div>
      </div>
    `;

    document.body.insertAdjacentHTML('beforeend', modalHTML);
  }

  setupHoverZoom() {
    if (this.isMobile) return;

    this.createZoomCursor();

    // Only apply cursor to currently visible gallery images
    this.visibleImages().forEach(img => {
      img.style.cursor = this.zoomCursorUrl;
    });
  }

  createZoomCursor() {
    if (window.productZoomSettings && window.productZoomSettings.customSvg) {
      const customSvg = window.productZoomSettings.customSvg.trim();
      this.zoomCursorUrl = `url("data:image/svg+xml;charset=utf-8,${encodeURIComponent(customSvg)}") 10 10, zoom-in`;
    } else if (window.productZoomSettings && window.productZoomSettings.cursorIcon) {
      let customIconUrl = window.productZoomSettings.cursorIcon;
      customIconUrl = customIconUrl.replace(/[?&]width=\d+/g, '');
      customIconUrl += (customIconUrl.includes('?') ? '&' : '?') + 'width=20';
      this.zoomCursorUrl = `url("${customIconUrl}") 10 10, zoom-in`;
    } else {
      const zoomCursorSvg = `
        <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 16 16" fill="none">
          <circle cx="7" cy="7" r="5" stroke="white" stroke-width="1.2"/>
          <path d="M14 14L11 11" stroke="white" stroke-width="1.2" stroke-linecap="round"/>
          <line x1="7" y1="4.5" x2="7" y2="9.5" stroke="white" stroke-width="1.2" stroke-linecap="round"/>
          <line x1="4.5" y1="7" x2="9.5" y2="7" stroke="white" stroke-width="1.2" stroke-linecap="round"/>
        </svg>
      `;
      this.zoomCursorUrl = `url("data:image/svg+xml;charset=utf-8,${encodeURIComponent(zoomCursorSvg)}") 8 8, zoom-in`;
    }
  }

  bindEvents() {
    document.addEventListener('click', (e) => {
      const galleryImage = e.target.closest(this.imgSelector);
      // Only respond to clicks on visible gallery images
      if (galleryImage && galleryImage.closest('.pdp-gallery--mobile, .pdp-gallery--desktop')?.offsetParent !== null) {
        e.preventDefault();
        this.openZoom(galleryImage);
      }
    });

    const modal = document.getElementById('zoom-modal');
    if (!modal) return;

    const closeBtn = modal.querySelector('.zoom-close');
    const overlay = modal.querySelector('.zoom-modal-overlay');

    closeBtn.addEventListener('click', () => this.closeZoom());
    overlay.addEventListener('click', () => this.closeZoom());

    // Keyboard navigation
    document.addEventListener('keydown', (e) => {
      if (!this.isZoomed) return;
      switch(e.key) {
        case 'Escape': this.closeZoom(); break;
        case 'ArrowUp': this.previousImage(); break;
        case 'ArrowDown': this.nextImage(); break;
      }
    });

    this.bindSwipeToClose(modal);

    modal.addEventListener('wheel', (e) => {
      if (this.isZoomed) {
        const imageContainer = modal.querySelector('.zoom-image-container');
        if (!imageContainer.contains(e.target)) {
          e.preventDefault();
        }
      }
    });
  }

  bindSwipeToClose(modal) {
    let touchStartY = 0;
    let touchStartX = 0;

    modal.addEventListener('touchstart', (e) => {
      touchStartY = e.touches[0].clientY;
      touchStartX = e.touches[0].clientX;
    }, { passive: true });

    modal.addEventListener('touchend', (e) => {
      const touchEndY = e.changedTouches[0].clientY;
      const touchEndX = e.changedTouches[0].clientX;
      const deltaY = touchEndY - touchStartY;
      const deltaX = Math.abs(touchEndX - touchStartX);

      if (deltaY > 80 && deltaX < 50) {
        this.closeZoom();
      }
    }, { passive: true });
  }

  openZoom(clickedImage) {
    // Collect only visible gallery images — no duplicates from hidden gallery
    this.images = this.visibleImages();
    this.currentImageIndex = this.images.indexOf(clickedImage);

    const modal = document.getElementById('zoom-modal');
    if (!modal) return;

    const zoomImage = document.getElementById('zoom-image');
    const loading = modal.querySelector('.zoom-loading');

    modal.style.display = 'flex';
    document.body.style.overflow = 'hidden';
    this.isZoomed = true;

    if (!this.isMobile) {
      this.createThumbnails();
    } else {
      const thumbnails = modal.querySelector('.zoom-thumbnails');
      if (thumbnails) thumbnails.style.display = 'none';
    }

    loading.style.display = 'block';
    zoomImage.style.display = 'none';

    this.loadZoomImage(clickedImage.src);

    requestAnimationFrame(() => {
      modal.style.opacity = '1';
    });
  }

  createThumbnails() {
    const thumbnailsContainer = document.querySelector('.zoom-thumbnails');
    if (!thumbnailsContainer) return;

    thumbnailsContainer.innerHTML = '';

    if (this.images.length <= 1) {
      thumbnailsContainer.style.display = 'none';
      return;
    }

    thumbnailsContainer.style.display = 'flex';

    this.images.forEach((image, index) => {
      const thumbnail = document.createElement('div');
      thumbnail.className = `zoom-thumbnail ${index === this.currentImageIndex ? 'active' : ''}`;

      const thumbnailImg = document.createElement('img');
      thumbnailImg.src = this.getThumbnailSrc(image.src);
      thumbnailImg.alt = image.alt || `Image ${index + 1}`;

      thumbnail.appendChild(thumbnailImg);
      thumbnail.addEventListener('click', () => this.goToImage(index));

      thumbnailsContainer.appendChild(thumbnail);
    });
  }

  getThumbnailSrc(src) {
    const baseUrl = src.split('?')[0];
    return `${baseUrl}?width=120`;
  }

  goToImage(index) {
    if (index === this.currentImageIndex) return;
    this.currentImageIndex = index;
    this.loadZoomImage(this.images[index].src);
    this.updateThumbnails();
  }

  updateThumbnails() {
    const thumbnails = document.querySelectorAll('.zoom-thumbnail');
    thumbnails.forEach((thumbnail, index) => {
      thumbnail.classList.toggle('active', index === this.currentImageIndex);
    });
  }

  closeZoom() {
    const modal = document.getElementById('zoom-modal');
    if (!modal) return;

    modal.style.opacity = '0';

    setTimeout(() => {
      modal.style.display = 'none';
      document.body.style.overflow = '';
      this.isZoomed = false;
    }, 300);
  }

  getHighResImageUrl(src) {
    const baseUrl = src.split('?')[0];

    if (baseUrl.includes('cdn.shopify.com') || baseUrl.includes('shopify')) {
      const cleanUrl = baseUrl.replace(/(_\d+x\d+)(\.[^.]+)$/, '$2');
      return `${cleanUrl}?width=2400`;
    }

    return baseUrl;
  }

  loadZoomImage(src) {
    const modal = document.getElementById('zoom-modal');
    if (!modal) return;

    const zoomImage = document.getElementById('zoom-image');
    const loading = modal.querySelector('.zoom-loading');

    const highResSrc = this.getHighResImageUrl(src);

    loading.style.display = 'block';
    zoomImage.style.display = 'none';

    const img = new Image();
    img.onload = () => {
      zoomImage.src = highResSrc;
      zoomImage.alt = this.images[this.currentImageIndex].alt || 'Product image';
      loading.style.display = 'none';
      zoomImage.style.display = 'block';
    };

    img.onerror = () => {
      const fallbackSrc = src.split('?')[0] + '?width=1800';

      const fallbackImg = new Image();
      fallbackImg.onload = () => {
        zoomImage.src = fallbackSrc;
        zoomImage.alt = this.images[this.currentImageIndex].alt || 'Product image';
        loading.style.display = 'none';
        zoomImage.style.display = 'block';
      };

      fallbackImg.onerror = () => {
        zoomImage.src = src;
        zoomImage.alt = this.images[this.currentImageIndex].alt || 'Product image';
        loading.style.display = 'none';
        zoomImage.style.display = 'block';
      };

      fallbackImg.src = fallbackSrc;
    };

    img.src = highResSrc;
  }

  previousImage() {
    if (this.images.length <= 1) return;
    this.currentImageIndex = this.currentImageIndex === 0
      ? this.images.length - 1
      : this.currentImageIndex - 1;
    this.loadZoomImage(this.images[this.currentImageIndex].src);
    this.updateThumbnails();
  }

  nextImage() {
    if (this.images.length <= 1) return;
    this.currentImageIndex = this.currentImageIndex === this.images.length - 1
      ? 0
      : this.currentImageIndex + 1;
    this.loadZoomImage(this.images[this.currentImageIndex].src);
    this.updateThumbnails();
  }
}

// Initialise when DOM is ready
document.addEventListener('DOMContentLoaded', () => {
  new ProductZoom();
});